import { useState } from "react";
import { useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router";
import { getInReviewDetailsQueryKey, getStageCompletionStatusQueryOptions, useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { getFundingApplicationStagesQueryOptions } from "../queries/funding-application-configuration/queries";
import {
  isRejectedFundingApplicationStateTransition,
  useFundingApplicationStateMutation,
  useFundingApplication,
} from "../queries/funding-application/queries";
import { NAVIGATION_PATHS } from "../components/constants";
import type { PAGE_TYPE } from "./navigation-next";

export interface UseApplicationSubmitOptions {
  currentPage: PAGE_TYPE;
  currentStep?: string;
}

export function useApplicationSubmit({ currentPage, currentStep }: UseApplicationSubmitOptions) {
  const {
    data: { customerDomain, featureFlags },
    refetch: refetchAppConfig,
  } = useCustomerAppConfig();
  const { data: stageCompletionStatus } = useSuspenseQuery(getStageCompletionStatusQueryOptions(customerDomain));
  const { data: stagesConfig } = useSuspenseQuery(getFundingApplicationStagesQueryOptions(customerDomain));
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const setApplicationState = useFundingApplicationStateMutation(customerDomain);
  const { navigate } = useSpaNavigate("FundingApp");
  const routerNavigate = useNavigate();
  const posthog = usePostHog();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigateAfterSubmission = (isHomeEnabled: boolean) => {
    if (isHomeEnabled) {
      sessionStorage.setItem("wf-home-welcome-banner", "true");
      routerNavigate(`/${customerDomain}/home`);
    } else {
      navigate(NAVIGATION_PATHS.IN_REVIEW);
    }
  };

  const submitApplication = async () => {
    if (isModalOpen) {
      return;
    }
    if (fundingApplication.applicationState === "submitted") {
      // Already submitted: the flag was latched at submission, so the loaded config is current.
      navigateAfterSubmission(featureFlags.enableHomePage ?? false);
      return;
    }
    const freshStatus = await queryClient
      .fetchQuery(getStageCompletionStatusQueryOptions(customerDomain))
      .catch(() => stageCompletionStatus);
    if (freshStatus.isSubmittable) {
      setApplicationState
        .mutateAsync({ applicationState: "submitted" })
        .then(async () => {
          posthog?.capture("onboarding_submission_started", {
            outcome: "submitted",
            areStagesSatisfied: freshStatus,
          });
          await queryClient.invalidateQueries({ queryKey: getInReviewDetailsQueryKey(customerDomain) });
          // Mutating the application to submitted sets the home-page discovery flag, so refetch
          const refetchResult = await refetchAppConfig();
          navigateAfterSubmission(refetchResult?.data?.featureFlags.enableHomePage ?? false);
        })
        .catch((error) => {
          if (isRejectedFundingApplicationStateTransition(error)) {
            // Hook's onError has already redirected to root.
            return;
          }
          throw error;
        });
    } else {
      posthog?.capture("onboarding_submission_started", {
        outcome: "blocked",
        areStagesSatisfied: freshStatus,
      });
      setIsModalOpen(true);
    }
  };

  const closeModal = () => setIsModalOpen(false);

  return {
    isSubmitting: setApplicationState.isPending,
    isModalOpen,
    stageCompletionStatus,
    stagesConfig,
    currentPage,
    currentStep,
    submitApplication,
    closeModal,
    navigate,
  };
}
